import EventEmitter from "./EventEmitter";

export interface ReadonlyObservableProperty<T> {
  getSnapshot(): T;
  /**
   * Listen for changes to the property.
   * Returns a function to unsubscribe the listener.
   */
  subscribe(handler: (value: T) => void): () => void;
}

/**
 * An observable property that emits change events when the value changes.
 * Useful as an external store for React components; useSyncExternalStore().
 * Important: Values should not be mutated after being set.
 */
export class ObservableProperty<T> implements ReadonlyObservableProperty<T> {
  private _value: T;
  private _changeEvent = new EventEmitter<T>();

  constructor(initialValue: T) {
    //Tricky bit:
    //Bind to allow passing the method as a callback, useful for passing
    //directly into React useSyncExternalStore() for example.
    this.getSnapshot = this.getSnapshot.bind(this);
    this.set = this.set.bind(this);
    this.subscribe = this.subscribe.bind(this);

    this._value = initialValue;
  }

  getSnapshot(): T {
    return this._value;
  }

  set(value: T) {
    if (this._value !== value) {
      this._value = value;
      this._changeEvent.emit(value);
    }
  }

  subscribe(handler: (value: T) => void): () => void {
    return this._changeEvent.addHandler(handler);
  }

  asReadonly(): ReadonlyObservableProperty<T> {
    return this;
  }
}

/**
 * A read-only observable whose value is derived by running a pure compute function
 * whenever any of the given source observables emit a change.
 *
 * Re-emits only when the computed value actually changes (strict equality check),
 * so downstream subscribers are not triggered unnecessarily.
 */
export class DerivedObservableProperty<T>
  implements ReadonlyObservableProperty<T>
{
  private _value: T;
  private readonly _changeEvent = new EventEmitter<T>();

  constructor(
    private readonly _compute: () => T,
    sources: ReadonlyObservableProperty<unknown>[],
  ) {
    this._value = _compute();
    this.getSnapshot = this.getSnapshot.bind(this);
    this.subscribe = this.subscribe.bind(this);

    sources.forEach((source) =>
      source.subscribe(() => {
        const next = this._compute();
        if (next !== this._value) {
          this._value = next;
          this._changeEvent.emit(next);
        }
      }),
    );
  }

  getSnapshot(): T {
    return this._value;
  }

  subscribe(handler: (value: T) => void): () => void {
    return this._changeEvent.addHandler(handler);
  }
}
